import { Avatar, Box, Button, DialogContent, DialogTitle, InputLabel, TextField, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { BaseRow, BaseSelect, BaseUploadFile } from '~/components/common';
import { InfoIcon, PictureIcon } from '~/assets/icons';
import { cast, handleApiError, notify, stringToColor } from '~/utils/helper';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { useFormik } from 'formik';
import { IProfile } from '~/utils/interface/auth';
import { DEPARTMENT } from '~/utils/constants/account';
import { UploadFile } from '~/utils/types/common';
import { handleEditProfile, handleGetProfile } from '~/thunks/auth/authThunk';
import { PERMISSIONS } from '~/utils/constants/permission';
import usePermission from '~/hooks/usePermission';

const initialValues: IProfile = {};

const Profile = () => {
  const { t } = useTranslation(['admin/account', 'admin/common']);

  const [isEditing, setIsEditing] = useState(false); // Toggle for edit mode

  const { profile } = useAppSelector((state) => state.auth);
  const permissions = usePermission();
  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(
          handleEditProfile({
            params: values,
          })
        ).unwrap();
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        setIsEditing(false);
        dispatch(handleGetProfile());
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false); //
  };
  useEffect(() => {
    form.setFieldValue('name', cast(profile?.name));

    form.setFieldValue('department', cast(profile?.department));
    form.setFieldValue('title', cast(profile?.title));
    form.setFieldValue('phone', cast(profile?.phone));

    form.setFieldValue('address', cast(profile?.address));

    form.setFieldValue('avatar', profile?.avatar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, profile]);
  return (
    <Box>
      <DialogTitle>
        <Box display='flex' alignItems='center' gap={1}>
          <PictureIcon />
          <Typography variant='h6'>{t('profilePicture')}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box className='gap-[20px] mb-[20px]'>
          <InputLabel>{t('avatar')}</InputLabel>
          {isEditing || form.values?.avatar ? (
            <BaseUploadFile
              disabled={!isEditing}
              uploadedFiles={typeof form.values?.avatar === 'string' ? [{ url: form.values?.avatar }] : undefined}
              onChange={(files: Array<UploadFile>) => {
                form.setFieldValue('avatar', files[0]?.file ?? '');
              }}
            />
          ) : (
            <Avatar sx={{ bgcolor: stringToColor(profile?.name ?? '') }}>{profile?.name?.[0]}</Avatar>
          )}
        </Box>
      </DialogContent>

      <DialogTitle>
        <Box display='flex' alignItems='center' gap={1}>
          <InfoIcon />
          <Typography variant='h6'>{t('personalInformation')}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('memberName')}</InputLabel>
              <TextField
                name='name'
                placeholder={t('memberName')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-100' : 'bg-white'} rounded-lg`}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('department')}</InputLabel>
              <BaseSelect
                name='department'
                value={form.values?.department}
                options={DEPARTMENT}
                onChange={form.handleChange}
                error={Boolean(form.errors?.department)}
                helperText={form.errors?.department}
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-100' : 'bg-white'} rounded-lg`}
              ></BaseSelect>
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('phoneNumber')}</InputLabel>
              <TextField
                name='phone'
                placeholder={t('phoneNumber')}
                value={form.values?.phone}
                onChange={form.handleChange}
                error={Boolean(form.errors?.phone)}
                helperText={form.errors?.phone}
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-100' : 'bg-white'} rounded-lg`}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('address')}</InputLabel>
              <TextField
                name='address'
                placeholder={t('address')}
                value={form.values?.address}
                onChange={form.handleChange}
                error={Boolean(form.errors?.address)}
                helperText={form.errors?.address}
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-100' : 'bg-white'} rounded-lg`}
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('titles')}</InputLabel>
              <TextField
                name='title'
                placeholder={t('titles')}
                value={form.values?.title}
                onChange={form.handleChange}
                error={Boolean(form.errors?.title)}
                helperText={form.errors?.title}
                disabled={!isEditing}
                className={`${!isEditing ? 'bg-gray-100' : 'bg-white'} rounded-lg`}
              />
            </Box>
            <Box className='w-full'></Box>
          </BaseRow>
          {permissions[PERMISSIONS.auth_edit] && (
            <Box className='justify-end flex'>
              {isEditing ? (
                <>
                  <Button className='text-[#758CA5] bg-[#F0F7FF] mr-[10px]' onClick={handleCancel}>
                    {t('cancel', { ns: 'admin/common' })}
                  </Button>
                  <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
                    {t('save', { ns: 'admin/common' })}
                  </Button>
                </>
              ) : (
                <Button color='primary' variant='contained' onClick={handleEdit}>
                  {t('edit', { ns: 'admin/common' })}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Box>
  );
};

export default memo(Profile);
